import { CONST_SOCIAL_THUMB_SIZE } from "@/utils/mediautils/mediautils";
import DailymotionIcon from "../../../assets/icons/svg/dailymotion-icon.svg?react";
import { DEFAULT_YOUTUBE_CATEGORIES } from "../../../constants";
import { SocialChannel } from "@/database";
import React from "react";

export type PlatformType =
  | "youtube"
  | "facebook"
  | "linkedin"
  | "twitter"
  | "instagram"
  | "dailymotion";

export interface PlatformConfig {
  platform: PlatformType;
  icon: string | React.ReactElement | React.ComponentType<any>;
  iconColor: string;
  description: string;
  titleLabel: string;
  titlePlaceholder: string;
  titleCharacterLimit: number;
  showDescription: boolean;
  descriptionCharacterLimit: number;
  showTags: boolean;
  showThumbnail: boolean;
  privacyOptions: Array<{ label: string; value: string }>;
  defaultPrivacy: string;
  showScheduler: boolean;
  showScheduleAfter: boolean;
  // Platform-specific fields
  showCategory?: boolean;
  showMonetization?: boolean;
  showPlaylist?: boolean;
  showKidsContent?: boolean;
  showRightsManagement?: boolean;
  // Validation
  validateTitle: (title: string) => boolean;
  validateDescription?: (desc: string) => boolean;
  validateDuration?: (duration: number) => { valid: boolean; error?: string };
  // Payload builder
  buildPayload: (
    values: any,
    initialdata: any,
    enabledFields: Record<string, boolean>
  ) => any;
}

const countGraphemes = (str: string) => {
  // Simple grapheme counter - you may want to use GraphemeSplitter here
  return str.length;
};

export const platformConfigs: Record<PlatformType, PlatformConfig> = {
  youtube: {
    platform: "youtube",
    icon: "bi-youtube",
    iconColor: "red",
    description: "Youtube channel and Youtube shorts",
    titleLabel: "TITLE",
    titlePlaceholder: "Enter title for publish",
    titleCharacterLimit: 100,
    showDescription: true,
    descriptionCharacterLimit: 5000,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [
      { label: "Public", value: "public" },
      { label: "Private", value: "private" },
      { label: "Unlisted", value: "unlisted" },
      { label: "Schedule", value: "schedule" },
    ],
    defaultPrivacy: "private",
    showScheduler: true,
    showScheduleAfter: true,
    showCategory: true,
    showMonetization: true,
    showPlaylist: true,
    showKidsContent: true,
    showRightsManagement: true,
    validateTitle: (title: string) => countGraphemes(title) <= 100,
    validateDescription: (desc: string) => countGraphemes(desc) <= 5000,
    validateDuration: (duration: number) => {
      if (duration < 1)
        return {
          valid: false,
          error: "YouTube: Video must be at least 1 second long",
        };
      return { valid: true };
    },
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      const payload: any = {
        job_type: "youtube",
        upload_type: "sftp",
        media: initialdata?.media_id,
        platform: values.id,
        title: values.title + "",
        id: initialdata?.published_id ?? "",
        desc: values.desc,
        tags: values.tags,
        privacy: values.privacy === "schedule" ? "private" : values.privacy,
        channel_name: values?.channel_name ?? "",
        playlist: values?.playlist ?? "",
        is_created_for_kids: values?.is_created_for_kids ?? "no",
        category: values?.category ?? "",
        is_rights_managements_allowed:
          values?.is_rights_managements_allowed ?? "",
        monetize_policy_id: values?.monetize_policy_id ?? "",
        monetize_policy_name: values?.monetize_policy_name ?? "",
        // conditional_rules: values?.conditional_rules ?? [],
      };
      if (values.thumb) {
        payload.thumb = values.thumb.includes("_180.jpg")
          ? values.thumb.replace("_180.jpg", CONST_SOCIAL_THUMB_SIZE)
          : values.thumb;
      }
      if (values.thumbId) {
        payload.thumb_id = values.thumbId;
      }
      if (values.schedule) {
        payload.schedule = values.schedule;
      }
      return payload;
    },
  },
  facebook: {
    platform: "facebook",
    icon: "bi-facebook",
    iconColor: "blue",
    description: "Facebook page",
    titleLabel: "TITLE",
    titlePlaceholder: "Enter title for publish",
    titleCharacterLimit: 95,
    showDescription: true,
    descriptionCharacterLimit: 5000,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [
      { label: "Public", value: "public" },
      { label: "Private", value: "private" },
      { label: "Schedule", value: "schedule" },
    ],
    defaultPrivacy: "public",
    showScheduler: true,
    showScheduleAfter: true,
    validateTitle: (title: string) => countGraphemes(title) <= 95,
    validateDescription: (desc: string) => {
      const length = countGraphemes(desc);
      return length >= 5 && length <= 5000;
    },
    validateDuration: (duration: number) => {
      if (duration < 1)
        return {
          valid: false,
          error: "Facebook: Video must be at least 1 second long",
        };
      if (duration > 240 * 60)
        return { valid: false, error: "Facebook: Max duration is 4 hours" };
      return { valid: true };
    },
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      const payload: any = {
        job_type: "facebook",
        upload_type: "api",
        media: initialdata.media_id,
        platform: values.id,
        title: values.title + "",
        desc: values.desc,
        tags: values.tags,

        privacy:
          values.privacy === "schedule"
            ? "private"
            : values.privacy || "private",
        channel_name: values?.channel_name ?? "",
        image: values.image,
      };
      if (values.thumb) {
        payload.thumb = values.thumb.includes("_180.jpg")
          ? values.thumb.replace("_180.jpg", CONST_SOCIAL_THUMB_SIZE)
          : values.thumb;
      }
      if (values.thumbId) {
        payload.thumb_id = values.thumbId;
      }
      if (values.schedule) {
        payload.schedule = values.schedule;
      }
      // Only status 0 (queue) was covered here - a retry on a FAILED
      // destination (status -1/-2) has a real `published_id` from its
      // earlier attempt too, but never got it echoed into the payload, so
      // the backend had nothing to re-attempt against. Any record that was
      // actually submitted before (queue, failed, or otherwise) carries
      // `published_id` - a brand-new draft never does - so gate on that
      // directly instead of enumerating every non-fresh status by hand,
      // same as YouTube's buildPayload above already does unconditionally.
      if (initialdata.published_id) {
        payload.id = initialdata.published_id;
      }
      return payload;
    },
  },
  linkedin: {
    platform: "linkedin",
    icon: "bi-linkedin",
    iconColor: "#0077B5",
    description: "LinkedIn profile",
    titleLabel: "TITLE",
    titlePlaceholder: "Enter title for publish",
    titleCharacterLimit: 1300,
    showDescription: true,
    descriptionCharacterLimit: 5000,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [
      { label: "Public", value: "public" },
      { label: "Connections", value: "connections" },
    ],
    defaultPrivacy: "public",
    showScheduler: false,
    showScheduleAfter: false,
    validateTitle: (title: string) => countGraphemes(title) <= 1300,
    validateDuration: (duration: number) => {
      if (duration < 3)
        return {
          valid: false,
          error: "LinkedIn: Video must be at least 3 seconds long",
        };
      if (duration > 600)
        return { valid: false, error: "LinkedIn: Max duration is 10 minutes" };
      return { valid: true };
    },
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      return {
        job_type: "linkedin",
        upload_type: "api",
        media: initialdata.media_id,
        platform: values.id,
        title: values.title + "",
        desc: values.desc,
        id: initialdata.published_id ?? "",
        tags: values.tags,
        privacy: values.privacy,
        channel_name: values?.channel_name ?? "",
        ...(values.thumb && {
          thumb: values.thumb.includes("_180.jpg")
            ? values.thumb.replace("_180.jpg", ".jpg")
            : values.thumb,
        }),
        ...(values.thumbId && { thumb_id: values.thumbId }),
      };
    },
  },
  twitter: {
    platform: "twitter",
    icon: "bi-twitter",
    iconColor: "#1DA1F2",
    description: "X profile",
    titleLabel: "TWEET",
    titlePlaceholder: "Enter tweet for publish",
    titleCharacterLimit: 280,
    showDescription: false,
    descriptionCharacterLimit: 0,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [{ label: "Public", value: "public" }],
    defaultPrivacy: "public",
    showScheduler: false,
    showScheduleAfter: false,
    validateTitle: (title: string) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const urls = title.match(urlRegex) || [];
      const urlCharCount = urls.length * 23;
      const textWithoutUrls = title.replace(urlRegex, "");
      const visibleLength = countGraphemes(textWithoutUrls);
      return visibleLength + urlCharCount <= 280;
    },
    validateDuration: (duration: number) => {
      if (duration < 1)
        return {
          valid: false,
          error: "X: Video must be at least 1 second long",
        };
      if (duration > 140)
        return {
          valid: false,
          error: "X: Max duration is 2 minutes 20 seconds",
        };
      return { valid: true };
    },
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      return {
        job_type: "twitter",
        upload_type: "api",
        media: initialdata.media_id,
        platform: values.id,
        title: values.title + "",
        desc: values.desc,
        id: initialdata.published_id ?? "",
        tags: values.tags,
        privacy: values.privacy,
        channel_name: values?.channel_name ?? "",
        ...(values.thumb && {
          thumb: values.thumb.includes("_180.jpg")
            ? values.thumb.replace("_180.jpg", ".jpg")
            : values.thumb,
        }),
        ...(values.thumbId && { thumb_id: values.thumbId }),
      };
    },
  },
  instagram: {
    platform: "instagram",
    icon: "bi-instagram",
    iconColor: "#E4405F",
    description: "Instagram account",
    titleLabel: "CAPTION",
    titlePlaceholder: "Enter caption for publish",
    titleCharacterLimit: 2200,
    showDescription: false,
    descriptionCharacterLimit: 0,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [{ label: "Public", value: "public" }],
    defaultPrivacy: "public",
    showScheduler: false,
    showScheduleAfter: false,
    validateTitle: (title: string) => {
      const length = countGraphemes(title);
      const hashtags = (title.match(/#[\p{L}\p{N}_]+/gu) || []).length;
      return length <= 2200 && hashtags <= 30;
    },
    validateDuration: (duration: number) => {
      if (duration < 3)
        return {
          valid: false,
          error: "Instagram: Video must be at least 3 seconds long",
        };
      // if (duration > 60)
      //   return { valid: false, error: "Instagram: Max duration is 60 seconds" };
      return { valid: true };
    },
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      return {
        job_type: "instagram",
        upload_type: "api",
        media: initialdata.media_id,
        platform: values.id,
        title: values.title + "",
        desc: values.desc,
        id: initialdata.published_id ?? "",
        tags: values.tags,
        privacy: values.privacy,
        channel_name: values?.channel_name ?? "",
        ...(values.thumb && {
          thumb: values.thumb.includes("_180.jpg")
            ? values.thumb.replace("_180.jpg", ".jpg")
            : values.thumb,
        }),
        ...(values.thumbId && { thumb_id: values.thumbId }),
      };
    },
  },
  dailymotion: {
    platform: "dailymotion",
    icon: DailymotionIcon as any, // Component type, will be handled in BasePublishCard
    iconColor: "#000000",
    description: "DailyMotion account",
    titleLabel: "TITLE",
    titlePlaceholder: "Enter title for publish",
    titleCharacterLimit: 1000,
    showDescription: true,
    descriptionCharacterLimit: 5000,
    showTags: true,
    showThumbnail: true,
    privacyOptions: [
      { label: "Public", value: "public" },
      { label: "Private", value: "private" },
    ],
    defaultPrivacy: "public",
    showScheduler: false,
    showScheduleAfter: false,
    validateTitle: (title: string) => countGraphemes(title) <= 1000,
    buildPayload: (
      values: any,
      initialdata: any,
      enabledFields: Record<string, boolean>
    ) => {
      return {
        job_type: "dailymotion",
        upload_type: "api",
        media: initialdata?.media_id,
        platform: values.id,
        title: values.title + "",
        desc: values.desc,
        id: initialdata?.published_id ?? "",
        tags: Array.isArray(values.tags)
          ? values.tags.slice(0, 15)
          : values.tags,
        privacy: values.privacy,
        channel_name: values?.channel_name ?? "",
        ...(values.thumb && {
          thumb: values.thumb.includes("_180.jpg")
            ? values.thumb.replace("_180.jpg", ".jpg")
            : values.thumb,
        }),
        ...(values.thumbId && { thumb_id: values.thumbId }),
      };
    },
  },
};

export const getPlatformConfig = (platform: string): PlatformConfig => {
  const normalizedPlatform = platform.toLowerCase() as PlatformType;
  return platformConfigs[normalizedPlatform] || platformConfigs.linkedin;
};

// Per-platform publish-payload shape: the job/upload type and the platform-specific fields a
// publish entry needs. Consumed by SocialPublish.buildPublish when a destination is added.
export const PLATFORM_PUBLISH_CONFIG = {
  youtube: {
    job_type: "youtube",
    upload_type: "sftp",
    getAdditionalProps: (channelDetails: SocialChannel) => ({
      channel_id: channelDetails.channel,
      playlist: channelDetails.playlist,
      is_rights_managements_allowed: channelDetails.right_management || "no",
      monetize_policy_id: channelDetails.monetization,
      category: channelDetails.category,
      is_created_for_kids: channelDetails.kids ? "yes" : "no",
      privacy: channelDetails.privacy || "private",
      isSFTPEnabled: channelDetails.is_sftp ?? 1,
    }),
  },
  facebook: {
    job_type: "facebook",
    upload_type: "api",
    getAdditionalProps: (channelDetails: SocialChannel) => ({
      pageName: channelDetails.name,
    }),
  },
  linkedin: {
    job_type: "linkedin",
    upload_type: "api",
    getAdditionalProps: () => ({}),
  },
  twitter: {
    job_type: "twitter",
    upload_type: "api",
    getAdditionalProps: () => ({}),
  },
  instagram: {
    job_type: "instagram",
    upload_type: "api",
    getAdditionalProps: () => ({}),
  },
  dailymotion: {
    job_type: "dailymotion",
    upload_type: "api",
    getAdditionalProps: () => ({}),
  },
} as const;
